/* KAMOK_ACCESSIBILITY_PRODUCT_CART_V2_1_20260727 */
(() => {
  const K = window.KamOkA11y;
  if (!K) return;

  const {
    text,
    unitWord,
    unitFromPrice,
    getProduct,
    productUrl,
    unitFromTags,
    productTitle,
    ensureStyles,
    moveSkipLinkFirst,
    localizeStaticParts,
    initDesktopMenu,
    initMobileMenu,
  } = K;

  function isVisuallyHidden(element) {
    if (!element) return true;
    const style = getComputedStyle(element);
    return style.display === 'none' || style.visibility === 'hidden' || element.hidden || element.offsetParent === null;
  }

  function syncCardSemantics(root = document) {
    root.querySelectorAll?.('.card-wrapper').forEach((card) => {
      const title = productTitle(card);

      card.querySelectorAll('.card__heading').forEach((heading) => {
        const hidden = isVisuallyHidden(heading);
        heading.setAttribute('aria-hidden', String(hidden));

        heading.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((control) => {
          if (hidden) {
            if (!control.hasAttribute('data-kamok-original-tabindex')) {
              control.setAttribute('data-kamok-original-tabindex', control.getAttribute('tabindex') ?? '');
            }
            control.setAttribute('tabindex', '-1');
            control.setAttribute('aria-hidden', 'true');
          } else {
            const original = control.getAttribute('data-kamok-original-tabindex');
            if (original !== null) {
              if (original === '') control.removeAttribute('tabindex');
              else control.setAttribute('tabindex', original);
              control.removeAttribute('data-kamok-original-tabindex');
            }
            control.removeAttribute('aria-hidden');
          }
        });
      });

      card.querySelectorAll('img').forEach((image) => {
        const current = (image.getAttribute('alt') || '').trim();
        if ((!current || current.toLowerCase() === 'image') && title) image.setAttribute('alt', title);
      });
    });
  }

  function fixPriceBadges(root = document) {
    root.querySelectorAll?.('.price.price--show-badge').forEach((price) => {
      const sale = price.querySelector('.price__badge-sale');
      const soldOut = price.querySelector('.price__badge-sold-out');
      const isSoldOut = price.classList.contains('price--sold-out');
      const isSale =
        price.classList.contains('price--on-sale') ||
        price.classList.contains('volume-pricing--sale-badge');

      if (sale) {
        const hidden = !isSale || isSoldOut;
        sale.hidden = hidden;
        sale.setAttribute('aria-hidden', String(hidden));
      }

      if (soldOut) {
        soldOut.hidden = !isSoldOut;
        soldOut.setAttribute('aria-hidden', String(!isSoldOut));
      }
    });
  }

  function fixProductImageAlt(root = document) {
    root.querySelectorAll?.('product-info img').forEach((image, index) => {
      const current = (image.getAttribute('alt') || '').trim();
      if (current && current.toLowerCase() !== 'image') return;

      const info = image.closest('product-info');
      const title = productTitle(info);
      if (title) image.setAttribute('alt', `${title} — ${text().image} ${index + 1}`);
    });
  }

  function setQuantityLabels(scope, note, unit, title) {
    const labels = text();
    const readableUnit = unitWord(unit);

    scope.querySelectorAll('input[type="number"]').forEach((input) => {
      const describedBy = new Set(
        (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
      );
      describedBy.add(note.id);
      input.setAttribute('aria-describedby', [...describedBy].join(' '));
      input.setAttribute('aria-label', labels.quantity(title || 'product', readableUnit));
    });

    scope.querySelectorAll('button[name="plus"]').forEach((button) => {
      button
        .querySelector('.visually-hidden')
        ?.replaceChildren(labels.increase(title || 'product', readableUnit));
    });

    scope.querySelectorAll('button[name="minus"]').forEach((button) => {
      button
        .querySelector('.visually-hidden')
        ?.replaceChildren(labels.decrease(title || 'product', readableUnit));
    });
  }

  function addUnitNote(container, scope, unit, title, short = false) {
    if (!container || !scope || !unit) return null;

    let note = container.querySelector(':scope > .kamok-sale-unit-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'kamok-sale-unit-note';
      note.id = `kamok-sale-unit-${Math.random().toString(36).slice(2, 10)}`;
      container.prepend(note);
    }

    note.textContent = short
      ? text().unitShort(unitWord(unit))
      : text().unitLong(unitWord(unit));
    setQuantityLabels(scope, note, unit, title);
    return note;
  }

  async function resolveUnit(scope) {
    const local = unitFromPrice(scope);
    if (local) return local;

    const product = await getProduct(productUrl(scope));
    return (
      unitFromTags(product?.tags) ||
      product?.variants?.[0]?.unit_price_measurement?.reference_unit ||
      ''
    );
  }

  async function enhanceProductPage(info) {
    if (!info || info.dataset.kamokUnitPending === 'true') return;
    info.dataset.kamokUnitPending = 'true';

    try {
      const unit = await resolveUnit(info);
      if (!unit) return;

      const quantityForm = info.querySelector('[id^="Quantity-Form-"]');
      if (quantityForm) addUnitNote(quantityForm, quantityForm, unit, productTitle(info), false);
    } finally {
      delete info.dataset.kamokUnitPending;
    }
  }

  async function enhanceCartItem(item) {
    if (!item || item.dataset.kamokUnitPending === 'true') return;
    item.dataset.kamokUnitPending = 'true';

    try {
      const unit = await resolveUnit(item);
      if (!unit) return;

      const details = item.querySelector('.cart-item__details') || item;
      addUnitNote(details, item, unit, productTitle(item), true);
    } finally {
      delete item.dataset.kamokUnitPending;
    }
  }

  function patchCartFocus() {
    customElements.whenDefined('cart-items').then(() => {
      const CartItemsClass = customElements.get('cart-items');
      const prototype = CartItemsClass?.prototype;
      if (!prototype?.enableLoading || prototype.enableLoading.kamokFocusPatched) return;

      const original = prototype.enableLoading;
      const patched = function patchedEnableLoading(line) {
        const nativeBlur = HTMLElement.prototype.blur;
        HTMLElement.prototype.blur = function suppressedBlur() {};

        try {
          return original.call(this, line);
        } finally {
          HTMLElement.prototype.blur = nativeBlur;
        }
      };

      patched.kamokFocusPatched = true;
      prototype.enableLoading = patched;
    });
  }

  function refresh(root = document) {
    moveSkipLinkFirst();
    localizeStaticParts();
    initDesktopMenu();
    initMobileMenu();
    syncCardSemantics(root);
    fixPriceBadges(root);
    fixProductImageAlt(root);

    /* V2 does not request product JSON for every card. */
    root.querySelectorAll?.('product-info').forEach(enhanceProductPage);
    root.querySelectorAll?.('.cart-item').forEach(enhanceCartItem);
  }

  function init() {
    ensureStyles();
    patchCartFocus();
    refresh();

    let queued = false;
    new MutationObserver((records) => {
      if (queued || !records.some((record) => record.addedNodes.length)) return;

      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        refresh();
      });
    }).observe(document.body, {
      subtree: true,
      childList: true,
    });

    addEventListener('resize', () => {
      syncCardSemantics();
      fixPriceBadges();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
